import { useState, useCallback, useEffect } from 'react';
import head from 'lodash/head';
import partial from 'lodash/partial';
import { ListGroup, ListGroupItem } from 'react-bootstrap';

import type { RevisionLicense, RevisionDependency } from '../../types';
import Button from '../generic/Button';
import Flex from '../generic/Flex';
import { icons } from '../generic/Icon';
import { joinOr, getAddAnotherText } from '../../utils';
import type { NewLicense } from '../modals/NewModalAddLicense';
import ModalAddLicense from '../modals/NewModalAddLicense';
import type { SingleRevisionDisplayMode } from '../modals/ModalDisplayLicense';
import ModalDisplayLicense from '../modals/ModalDisplayLicense';
import type {
  LicenseGroups,
} from './groups';
import {
  addLicense,
  addLicenseRawText,
  deleteLicense,
  getLicenseGroupsFromRevision,
  removeLicenseRawText,
  setLicenseCopyright,
  setLicenseText,
  setLicenseTitle,
  splitLicense,
  toggleEditGroup,
} from './groups';
import { isCustomLicense } from '../../../shared/licenses';

export type Change = 'edit' | 'split';

type LicenseFormProps = {
  license: RevisionLicense;
  hasMultipleLicenses: boolean;
  canEdit: boolean;
  canSplit?: boolean;
  onChangeBtnClick: () => void;
  onDelete: () => void;
  onAddRawText: () => void;
  onRemoveRawText: () => void;
  onSplit: () => void;
  onTitleChange: (title: string) => void;
  onTextChange: (text: string) => void;
  onCopyrightChange: (copyright: string) => void;
};

const LicenseForm = ({
  license,
  hasMultipleLicenses,
  canEdit,
  canSplit = true,
  onChangeBtnClick,
  onDelete,
  onAddRawText,
  onRemoveRawText,
  onSplit,
  onTitleChange,
  onTextChange,
  onCopyrightChange,
}: LicenseFormProps) => {
  const handleTitleChange = useCallback((evt) => onTitleChange(evt.target.value), [onTitleChange]);
  const handleTextChange = useCallback((evt) => onTextChange(evt.target.value), [onTextChange]);
  const handleCopyrightChange = useCallback((evt) => onCopyrightChange(evt.target.value), [onCopyrightChange]);
  const hasCustomText = !!(license.text || license.copyright || license.showRaw);
  return (
    <div className="edit-dep-license-group">
      {isCustomLicense(license.licenseId ?? '') ? (
        <input
          style={{ marginTop: '5px', marginBottom: '5px' }}
          type="text"
          className="input-block-level"
          placeholder="my-custom-license-title"
          onChange={handleTitleChange}
          value={license.title}
          disabled={!canEdit}
        />
      ) : (
        <p className="margin-bottom-sm">
          {license.title || license.licenseId}
        </p>
      )}
      {hasCustomText && (
        <div className="license-group-custom-text margin-bottom-md">
          <textarea
            onChange={handleTextChange}
            className="input-block-level dep-field-fulltext-edit"
            placeholder="Add raw license text..."
            value={license.text}
            disabled={!canEdit}
          />
          <input
            style={{marginTop: '5px'}}
            type="text"
            className="input-block-level"
            placeholder="... OR, add a copyright header"
            onChange={handleCopyrightChange}
            value={license.copyright}
            disabled={!canEdit}
          />
        </div>
      )}
      <Flex justify="flex-start">
        <span className="margin-right-sm">
          <Button
            icon={icons.pencil}
            size="small"
            type="primary"
            onClick={onChangeBtnClick}
            isDisabled={!canEdit}
          >
            Change
          </Button>
        </span>
        <span className="margin-right-sm">
          <Button
            icon={icons.trash}
            size="small"
            type="error"
            onClick={onDelete}
            isDisabled={!canEdit}
          >
            Delete
          </Button>
        </span>
        <span className="margin-right-sm">
          <Button
            icon={hasCustomText ? icons.minusSquareO : icons.plusSquareO}
            size="small"
            type="secondary"
            onClick={hasCustomText ? onRemoveRawText : onAddRawText}
            isDisabled={!canEdit}
          >
            {hasCustomText ? 'Remove' : 'Add'} Raw Text
          </Button>
        </span>
        {hasMultipleLicenses && canSplit && (
          <Button
            icon={icons.export}
            size="small"
            type="secondary"
            onClick={onSplit}
            isDisabled={!canEdit}
          >
            Split from Group
          </Button>
        )}
      </Flex>
    </div>
  );
};

type EditableListProps = {
  defaultLicenseGroups?: LicenseGroups;
  revision?: RevisionDependency;
  canEdit: boolean;
  onChange: (licenseGroups: LicenseGroups, change: Change) => void;
  // optional parameters for finer-grained UI control to facilitate ModalAddDependency
  canSplit?: boolean; // enabled/disable user's ability to split a license groups
  hideLabel?: boolean; // show/hide the "Licenses" label in the UI
  hideFileMatches?: boolean; // show/hide the "View file matches" button
  shouldSkipWarning?: boolean; // disable the warning alert after adding a license
  maxLicenseGroups?: number; // set the maximum amount of LicenseGroups allowed for the user to add
}

const EditableList = ({
  defaultLicenseGroups,
  canEdit,
  onChange,
  revision,
  // optional parameters for finer-grained UI control to facilitate ModalAddDependency
  canSplit = true,
  hideLabel = false,
  hideFileMatches = false,
  shouldSkipWarning = false,
  maxLicenseGroups,
}: EditableListProps) => {
  const [licenseGroups, setLicenseGroups] = useState(() =>
    defaultLicenseGroups || (revision && getLicenseGroupsFromRevision(revision)) || []);
  useEffect(() => {
    setLicenseGroups(defaultLicenseGroups || (revision && getLicenseGroupsFromRevision(revision)) || []);
  }, [defaultLicenseGroups, revision]);

  const [focusLicenses, setFocusLicenses] = useState([]);
  const [licensePick, setLicensePick] = useState({
    showModal: false,
    groupIndex: null,
    licenseIndex: null,
  });

  // The modal is shown when changing a license, adding a license to a group, adding a new license group.
  const handleShowPickLicenseModal = useCallback((groupIndex, licenseIndex) => {
    setLicensePick({
      showModal: true,
      groupIndex,
      licenseIndex: licenseIndex,
    });
  }, []);

  const handleHidePickLicenseModal = useCallback(() => {
    setLicensePick({
      showModal: false,
      groupIndex: null,
      licenseIndex: null,
    });
  }, []);

  const handleChange = useCallback((groups: LicenseGroups, change: Change = 'edit') => {
    setLicenseGroups(groups);
    onChange(groups, change);
  }, [onChange]);

  // ModalAddLicense.onAddLicense expects a function that returns a promise, so we make this function async.
  const handleAddLicense = useCallback(async (license: NewLicense) => {
    const revisionLicense: Partial<RevisionLicense> = {
      licenseId: license.licenseId,
    };

    if (license.licenseTitle) {
      revisionLicense.title = license.licenseTitle;
    }
    if (license.licenseText) {
      revisionLicense.text = license.licenseText;
    }
    if (license.copyright) {
      revisionLicense.copyright = license.copyright;
    }

    setLicensePick({
      showModal: false,
      groupIndex: null,
      licenseIndex: null,
    });
    return handleChange(addLicense(licenseGroups, licensePick.groupIndex, licensePick.licenseIndex, revisionLicense));
  }, [licenseGroups, licensePick]);

  const licenseActionButton = (editing: boolean, groupIndex: number): string => {
    let icon;
    let text;

    if (editing) {
      icon = icons.minusSquareO;
      text = 'Hide';
    } else if (canEdit) {
      icon = icons.pencil;
      text = 'Edit';
    } else {
      icon = icons.eye;
      text = 'View';
    }

    return (
      <Button
        type="primary"
        size="medium"
        icon={icon}
        onClick={() => handleChange(toggleEditGroup(licenseGroups, groupIndex))}
      >
        {text}
      </Button>
    );
  };

  const licenseDisplayMode: SingleRevisionDisplayMode | null = focusLicenses.length > 0
    ? { mode: 'single', revisionId: revision?.locator as string, revisionLicenses: focusLicenses }
    : null;

  return (
    <div className="form-group">
      <div className="col-md-12 margin-top-lg license-edit-container">
        {!hideLabel && <label>Licenses</label>}
        <ListGroup>
          {
            licenseGroups.map(({ licenses, editing }, groupIndex) => (
              <ListGroupItem key={groupIndex}>
                <div style={{ padding: '9px 0px' }} className="oneline bold">
                  {joinOr(licenses.map(l => {
                    if (isCustomLicense(l.licenseId ?? '') && l.title) {
                      return l.title as string;
                    }
                    return l.licenseId as string;
                  }))}
                </div>
                {licenses.every(l => l.ignored) ? (
                  <div>
                    <span className="label label-danger">Ignored</span>
                  </div>
                ) : (
                  <div className="flex-center">
                    {!hideFileMatches && head(licenses).licenseId && (
                      <span className="margin-right-sm">
                        <Button
                          type="secondary"
                          size="medium"
                          icon={icons.search}
                          onClick={() => setFocusLicenses(licenses)}
                        >
                          View File Matches
                        </Button>
                      </span>
                    )}
                    {licenseActionButton(editing, groupIndex)}
                  </div>)
                }
                {editing && (
                  <div className="license-group-licenses">
                    {licenses.map((license, licenseIndex) => (
                      <LicenseForm
                        key={licenseIndex}
                        license={license as RevisionLicense}
                        hasMultipleLicenses={licenses.length > 1}
                        canEdit={canEdit}
                        canSplit={canSplit}
                        onChangeBtnClick={partial(handleShowPickLicenseModal, groupIndex, licenseIndex)}
                        onDelete={() => handleChange(deleteLicense(licenseGroups, groupIndex, licenseIndex))}
                        onAddRawText={() => handleChange(addLicenseRawText(licenseGroups, groupIndex, licenseIndex))}
                        onRemoveRawText={() => handleChange(
                          removeLicenseRawText(licenseGroups, groupIndex, licenseIndex),
                        )}
                        onSplit={() => handleChange(
                          splitLicense(licenseGroups, groupIndex, licenseIndex, license as RevisionLicense),
                          'split',
                        )}
                        onTitleChange={(title) => handleChange(
                          setLicenseTitle(title, licenseGroups, groupIndex, licenseIndex),
                        )}
                        onTextChange={(text) => handleChange(
                          setLicenseText(text, licenseGroups, groupIndex, licenseIndex),
                        )}
                        onCopyrightChange={(copyright) => handleChange(
                          setLicenseCopyright(copyright, licenseGroups, groupIndex, licenseIndex),
                        )}
                      />
                    ))}
                    <div className="margin-top-sm">
                      <Button
                        size="large"
                        type="secondary"
                        icon={icons.plusSquareO}
                        onClick={partial(handleShowPickLicenseModal, groupIndex, null)}
                        isDisabled={!canEdit}
                      >
                        {getAddAnotherText('license', licenses.length)}
                      </Button>
                    </div>
                  </div>
                )}
              </ListGroupItem>
            ))
          }
        </ListGroup>
        {(!maxLicenseGroups || licenseGroups.length < maxLicenseGroups) &&
          <Button
            size="large"
            type="secondary"
            icon={icons.plusSquareO}
            onClick={partial(handleShowPickLicenseModal, licenseGroups.length, null)}
            isDisabled={!canEdit}
          >
            {getAddAnotherText('license group', licenseGroups.length)}
          </Button>}
        {licensePick.showModal && (
          <ModalAddLicense
            onCancel={handleHidePickLicenseModal}
            onSave={handleAddLicense}
            shouldSkipWarning={shouldSkipWarning}
            isNested
          />
        )}
        <ModalDisplayLicense
          displayMode={licenseDisplayMode}
          revisionPackageCacheError={revision?.package_cache_error}
          onHide={() => setFocusLicenses([])}
        />
      </div>
    </div>
  );
};

export default EditableList;
